import React, { useState, useEffect } from "react";
import { nmmcCardService } from "../services/nmmcCardService";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://newgardenbackend.amcfest.in/api";

const NmmcCardManagement = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [filterCardType, setFilterCardType] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [formData, setFormData] = useState({
    card_type: "announcements",
    title: "",
    name: "",
    designation: "",
    description: "",
    link: "",
    link_text: "",
    display_order: 0,
    is_active: 1,
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [viewImageModal, setViewImageModal] = useState({
    show: false,
    src: "",
    title: "",
  });

  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
  const placeholderImageLarge = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236b7280'%3EImage Not Found%3C/text%3E%3C/svg%3E";

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // Replace localhost URLs with production URL
    if (imagePath.includes("localhost:4001")) {
      imagePath = imagePath.replace(/http:\/\/localhost:4001/g, "https://newgardenbackend.amcfest.in");
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    
    // Get base URL without /api
    const baseUrl = API_BASE_URL.replace("/api", "");
    
    // If it starts with /, it's a relative path from the base
    if (imagePath.startsWith("/")) {
      return `${baseUrl}${imagePath}`;
    }
    
    // If it contains uploads, ensure proper path
    if (imagePath.includes("uploads")) {
      // Remove leading slash if present to avoid double slashes
      const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
      return `${baseUrl}${cleanPath}`;
    }
    
    // Otherwise, assume it's relative to the base
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    loadCards();
  }, [filterCardType, filterActive]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const response = await nmmcCardService.getAllNmmcCards(
        filterCardType || null,
        filterActive !== "" ? filterActive : null
      );
      // Handle different response formats
      let cardsData = [];
      if (Array.isArray(response)) {
        cardsData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        cardsData = response.data;
      } else if (response?.success && Array.isArray(response.data)) {
        cardsData = response.data;
      }
      
      setCards(cardsData);
    } catch (error) {
      console.error("Error loading NMMC cards:", error);
      alert("Failed to load NMMC cards");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append("card_type", formData.card_type);
      formDataToSend.append("title", formData.title || "");
      formDataToSend.append("name", formData.name || "");
      formDataToSend.append("designation", formData.designation || "");
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("link", formData.link || "");
      formDataToSend.append("link_text", formData.link_text || "");
      formDataToSend.append("display_order", formData.display_order || 0);
      formDataToSend.append("is_active", formData.is_active ? 1 : 0);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      if (editingCard) {
        await nmmcCardService.updateNmmcCard(editingCard.id, formDataToSend);
        alert("NMMC Card updated successfully");
      } else {
        await nmmcCardService.addNmmcCard(formDataToSend);
        alert("NMMC Card added successfully");
      }

      resetForm();
      loadCards();
    } catch (error) {
      console.error("Error saving NMMC card:", error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to save NMMC card"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setFormData({
      card_type: card.card_type || "announcements",
      title: card.title || "",
      name: card.name || "",
      designation: card.designation || "",
      description: card.description || "",
      link: card.link || "",
      link_text: card.link_text || "",
      display_order: card.display_order || 0,
      is_active: card.is_active !== undefined ? card.is_active : 1,
      image: null,
    });
    setImagePreview(card.image ? getImageUrl(card.image) : null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this NMMC card?")) {
      return;
    }
    try {
      setLoading(true);
      await nmmcCardService.deleteNmmcCard(id);
      alert("NMMC Card deleted successfully");
      loadCards();
    } catch (error) {
      console.error("Error deleting NMMC card:", error);
      alert("Failed to delete NMMC card");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      card_type: "announcements",
      title: "",
      name: "",
      designation: "",
      description: "",
      link: "",
      link_text: "",
      display_order: 0,
      is_active: 1,
      image: null,
    });
    setImagePreview(null);
    setEditingCard(null);
    setShowForm(false);
  };

  const getCardTypeLabel = (type) => {
    const labels = {
      announcements: "ANNOUNCEMENTS",
      officers: "NMMC OFFICERS",
      about: "ABOUT NMMC",
    };
    return labels[type] || type.toUpperCase();
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">
            NMMC Card Management
          </h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            {showForm ? "Cancel" : "Add New Card"}
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <select
            value={filterCardType}
            onChange={(e) => setFilterCardType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Card Types</option>
            <option value="announcements">ANNOUNCEMENTS</option>
            <option value="officers">NMMC OFFICERS</option>
            <option value="about">ABOUT NMMC</option>
          </select>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          {(filterCardType || filterActive) && (
            <button
              onClick={() => {
                setFilterCardType("");
                setFilterActive("");
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-semibold mb-4">
                {editingCard ? "Edit NMMC Card" : "Add New NMMC Card"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="card_type"
                    value={formData.card_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="announcements">ANNOUNCEMENTS</option>
                    <option value="officers">NMMC OFFICERS</option>
                    <option value="about">ABOUT NMMC</option>
                  </select>
                </div>

                {formData.card_type === "announcements" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter card title (e.g., ANNOUNCEMENTS)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter description (e.g., No Announcements)"
                      />
                    </div>
                  </>
                )}

                {formData.card_type === "officers" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Card Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter card title (e.g., NMMC OFFICERS)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Officer Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter officer name (e.g., Dr. Kailas Shinde (IAS))"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        rows={3}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter designation (e.g., Commissioner and Administrator...)"
                      />
                    </div>
                  </>
                )}

                {formData.card_type === "about" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Card Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter card title (e.g., ABOUT NMMC)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={5}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter description about NMMC"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link URL
                      </label>
                      <input
                        type="url"
                        name="link"
                        value={formData.link}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter link URL (optional)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link Text
                      </label>
                      <input
                        type="text"
                        name="link_text"
                        value={formData.link_text}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter link text (e.g., Read More)"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image
                    {formData.card_type === "officers" && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    required={formData.card_type === "officers" && !editingCard}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {(imagePreview || (editingCard && editingCard.image)) && (
                    <div className="mt-4">
                      <img
                        src={
                          imagePreview ||
                          getImageUrl(editingCard.image)
                        }
                        alt="Preview"
                        className="max-w-xs h-48 object-cover rounded-lg border border-gray-300 shadow-md"
                        onError={(e) => {
                          if (e.target.src !== placeholderImageLarge) {
                            e.target.src = placeholderImageLarge;
                          }
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      name="is_active"
                      value={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === "1" ? 1 : 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : editingCard
                      ? "Update"
                      : "Add"}{" "}
                    Card
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Cards List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading && !showForm ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading cards...</p>
            </div>
          ) : cards.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No NMMC cards found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Title/Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Content
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cards.map((card) => {
                    // Check if image exists and is not empty/null
                    const hasImage = card.image && card.image.trim() !== "" && card.image !== "null";
                    const imageUrl = hasImage ? getImageUrl(card.image) : null;
                    return (
                    <tr key={card.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {card.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getCardTypeLabel(card.card_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={card.title || card.name || "Card"}
                            className="h-20 w-20 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity shadow-md"
                            onClick={() => {
                              setViewImageModal({
                                show: true,
                                src: imageUrl,
                                title: card.title || card.name || "Card Image",
                              });
                            }}
                            onError={(e) => {
                              // If image fails to load, show placeholder
                              if (e.target.src !== placeholderImage) {
                                e.target.src = placeholderImage;
                                e.target.onclick = null;
                              }
                            }}
                          />
                        ) : (
                          <div className="h-20 w-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500 border border-gray-300">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {card.title || card.name || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        <div
                          className="truncate"
                          title={card.description || card.designation || "N/A"}
                        >
                          {card.description || card.designation || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {card.display_order || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            card.is_active
                              ? "bg-blue-100 text-blue-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {card.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(card)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(card.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Image View Modal */}
        {viewImageModal.show && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() =>
              setViewImageModal({ show: false, src: "", title: "" })
            }
          >
            <div
              className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">
                  {viewImageModal.title}
                </h3>
                <button
                  onClick={() =>
                    setViewImageModal({ show: false, src: "", title: "" })
                  }
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <img
                  src={viewImageModal.src}
                  alt={viewImageModal.title}
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.src = placeholderImageLarge;
                    e.target.onerror = null;
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NmmcCardManagement;
